import { StatusBadge, STATUS_BADGES } from "@/shared/components/StatusBadge";
import { useDateTimeFormat } from "@/shared/datetime";
import type { OrgLlmConnectionRecord } from "@/shared/domain/orgs";
import { useVerifyOrgLlmConnection } from "@/shared/domain/orgs/hooks";
import { ORG_LLM_CONNECTION_KIND_LABELS } from "@/shared/domain/orgs/orgModelConfig";
import { RobotoButton } from "@/shared/foundation/primitives";

import { verifiedWhen } from "../verification";
import { VerifyOutcome } from "../VerifyOutcome";

import styles from "./OrgLlmConnections.module.css";

/** Where a connection sends its calls; an Anthropic API connection without a gateway goes to Anthropic. */
function endpointLabel(connection: OrgLlmConnectionRecord): string {
  const { settings } = connection;
  if (settings.base_url !== null && settings.base_url !== undefined) {
    return settings.base_url;
  }
  return "https://api.anthropic.com (default)";
}

interface ConnectionListItemProps {
  orgId: string;
  connection: OrgLlmConnectionRecord;
  /** Admins of a premium org may verify it: call a model on it and record the outcome. */
  canVerify: boolean;
  /** Omitted when the caller may not edit it. */
  onEdit?: () => void;
  /** Omitted when the caller may not delete it. */
  onDelete?: () => void;
}

/** One connection: its kind, endpoint, key secret, status and verification. */
export function ConnectionListItem({
  orgId,
  connection,
  canVerify,
  onEdit,
  onDelete,
}: ConnectionListItemProps) {
  const verifyMutation = useVerifyOrgLlmConnection();
  const dateTimeFormat = useDateTimeFormat();
  return (
    <li className={styles.item} aria-label={connection.label}>
      <div className={styles.itemHeader}>
        <span className={styles.itemLabel}>{connection.label}</span>
        <StatusBadge status={STATUS_BADGES.llmConnection[connection.status]} />
        {connection.last_verified_at === null ? (
          <StatusBadge
            status={STATUS_BADGES.llmConnectionVerification.not_verified}
          />
        ) : (
          <StatusBadge
            status={STATUS_BADGES.llmConnectionVerification.verified}
            tooltip={verifiedWhen(connection.last_verified_at, dateTimeFormat)}
          />
        )}
        <div className={styles.itemActions}>
          {canVerify && (
            <RobotoButton
              variant="ghost"
              size="sm"
              loading={verifyMutation.isPending}
              onClick={() =>
                verifyMutation.mutate({
                  orgId,
                  connectionId: connection.connection_id,
                })
              }
              aria-label={`Verify ${connection.label}`}
            >
              Verify
            </RobotoButton>
          )}
          {onEdit !== undefined && (
            <RobotoButton
              variant="ghost"
              size="sm"
              onClick={onEdit}
              aria-label={`Edit ${connection.label}`}
            >
              Edit
            </RobotoButton>
          )}
          {onDelete !== undefined && (
            <RobotoButton
              variant="ghost"
              size="sm"
              onClick={onDelete}
              aria-label={`Delete ${connection.label}`}
            >
              Delete
            </RobotoButton>
          )}
        </div>
      </div>
      <dl className={styles.details}>
        <dt>Kind</dt>
        <dd>{ORG_LLM_CONNECTION_KIND_LABELS[connection.settings.kind]}</dd>
        <dt>Endpoint</dt>
        <dd>{endpointLabel(connection)}</dd>
        <dt>API key secret</dt>
        <dd>{connection.settings.api_key_secret_name ?? "None"}</dd>
      </dl>
      {connection.last_error !== null && (
        <p className={styles.error}>Last error: {connection.last_error}</p>
      )}
      <VerifyOutcome
        result={verifyMutation.data}
        error={verifyMutation.error}
      />
    </li>
  );
}
